import React, { useContext, useEffect, useState } from "react"
import { PatientContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";
import { translateSymptom, SYMPTOM_CATEGORIES } from "../../i18n/translateData";

import { Row, Col } from "react-bootstrap";
import Nav from 'react-bootstrap/Nav';
import Tab from 'react-bootstrap/Tab';

import styled from "styled-components"

import {
    removeTrackedFactors,
    postPausedList,
} from "../../api/TBIRequests";

const Container = styled.div`
  // width: 280px;
  margin: 10px;
  h6 {
    text-align: center;
  }
  ul {
    font-weight: normal;
    font-size: 15px;
    margin: 10px;
  }
`

const UITrackingList = ({ symptoms, pauseList, setPauseList, pausedFactorObj, setPausedFactorObj, setTopSymptoms, setLastPaused, lastUnpaused, title }) => {
    const { patient } = useContext(PatientContext);
    const { t } = useTranslation();

    // Controlled tab selection (null = fall back to the first category with symptoms)
    const [activeKey, setActiveKey] = useState(null);
    // Factor of the symptom that was just unpaused — briefly highlighted
    const [highlighted, setHighlighted] = useState(null);

    // When a symptom is unpaused, jump to its category and flash it for 3s
    useEffect(() => {
        if (!lastUnpaused?.subcategory) return;
        setActiveKey(lastUnpaused.subcategory);
        setHighlighted(lastUnpaused.factor);
        const timer = setTimeout(() => setHighlighted(null), 3000);
        return () => clearTimeout(timer);
    }, [lastUnpaused]);

    const addPauseFactor = (newone) => {
        let newParsedFactors = [];
        if (pauseList) {
            if (!pauseList.includes(newone)) {
                newParsedFactors = [...pauseList, newone];
                const objWithFactor = symptoms?.find((symptom) => symptom.factor === newone);
                const newPausedFactorObj = [...pausedFactorObj, objWithFactor]
                setPausedFactorObj(newPausedFactorObj)
                // Tell the "Symptoms Paused" section which symptom was just paused,
                // so it can switch to that category and highlight it.
                // pausedAt makes pausing the same symptom again still retrigger it.
                if (setLastPaused && objWithFactor) {
                    setLastPaused({ ...objWithFactor, pausedAt: Date.now() });
                }
            } else {
                newParsedFactors = [...pauseList];
            }
        } else {
            newParsedFactors = [newone];
        }
        setPauseList(newParsedFactors);
        postPausedList({
            patientId: patient.patientId,
            data: [[newone]],
        }).catch(() => {
            console.log("postPausedList error");
        })
    }

    const tabs = Object.fromEntries(
        SYMPTOM_CATEGORIES.map((c) => [c, t(`onboarding.symptomSelector.category.${c}`)])
    );

    // Display only — the English `factor` is what's sent to the backend.
    // Apostrophe mismatches are handled inside translateSymptom.
    const translateSymptomLabel = (factor, subcategory) => translateSymptom(t, factor, subcategory);

    const removeTracking = (s) => {
        // symptoms
        let newTopSymptoms = [];
        symptoms.forEach((sy) => {
            if (sy.factor != s) {
                newTopSymptoms.push(sy);
            }
        })
        setTopSymptoms(newTopSymptoms);
        removeTrackedFactors({
            patientId: patient.patientId,
            data: [s],
        }).catch(() => {
            console.log("removeTrackedFactors error");
        })
    }

    const uniqueSymptoms = [];
    const factors = new Set();

    for (const symptom of symptoms) {
        if (!factors.has(symptom.factor)) {
            factors.add(symptom.factor);
            uniqueSymptoms.push(symptom);
        }
    }

    // Create a Set to store unique subcategories
    const uniqueSubcategories = new Set();

    // Filter and populate the Set with unique subcategories
    uniqueSymptoms.forEach((symptom) => {
        uniqueSubcategories.add(symptom.subcategory);
    });

    // Convert the Set to an array (if needed)
    const uniqueSubcategoryArray = Array.from(uniqueSubcategories).sort(
        (a, b) => Object.keys(tabs).indexOf(a) - Object.keys(tabs).indexOf(b)
    );

    // Never show an empty pane: if the selected category has no symptoms left
    // (or nothing was selected yet), fall back to the first category that does.
    const resolvedKey = uniqueSubcategoryArray.includes(activeKey)
        ? activeKey
        : uniqueSubcategoryArray[0];

    const colors = {
        motor: "#b3e5fc",
        sensory: "#ffccbc",
        emotional: "#dab7ec",
        behavioral: "#ffdadb",
        cognitive: "#ffdcaf",
        immune: "#fff3c4",
        metabolic: "#cef3c4",

        // for trigger
        wellness: "#dab7ec",
        travel: "#ffdadb",
        "screen time": "#ffdcaf",
        environmental: "#fff3c4",
        Dietary: "#cef3c4",
        situational: "#c1fdf7",

        // for therapies
        Alternative: "#dab7ec",
        Applied: "#ffdadb",
        Chiropractic: "#ffdcaf",
        Vision: "#fff3c4",
        "Mental Health": "#c1fdf7",

        Other: "#D3D3D3",
    };

    return (
        <Container>
            <h6>{title}</h6>
            <Tab.Container
                id="tracking-symptoms-tabs"
                activeKey={resolvedKey}
                onSelect={(k) => setActiveKey(k)}
            >
                <Row style={{ marginTop: "0.5rem", fontWeight: "normal", fontSize: "15px" }}>
                    <Col sm={3} style={{ paddingRight: '0px' }}>
                        <Nav fill justify variant="tabs" className="flex-column">
                            {uniqueSubcategoryArray.map((subcategory, i) => (
                                <Nav.Item key={i}
                                >
                                    <Nav.Link className="navLink" eventKey={subcategory}
                                        style={{
                                            background: `${colors[subcategory]}`,
                                            border: subcategory === resolvedKey ? '3px solid #106995' : '1px solid #106995',
                                            fontWeight: subcategory === resolvedKey ? 700 : 400,
                                        }}> {tabs[subcategory]}</Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                    </Col>
                    <Col sm={7} style={{
                        marginLeft: '1rem', paddingRight: '0px'
                    }}>
                        <Tab.Content>
                            {uniqueSubcategoryArray.map((subcategory, i) => (
                                <Tab.Pane eventKey={subcategory} key={i}>
                                    <h6 className="d-inline">{tabs[subcategory]}</h6>
                                    <ul>
                                        {uniqueSymptoms
                                            .filter((symptom) => symptom.subcategory === subcategory)
                                            .map((filteredSymptom, j) => (
                                                <li
                                                    key={j}
                                                    style={{
                                                        backgroundColor: filteredSymptom.factor === highlighted ? '#fff3c4' : 'transparent',
                                                        transition: 'background-color 0.6s ease',
                                                        borderRadius: '4px',
                                                        padding: '2px 4px',
                                                    }}
                                                >
                                                    {translateSymptomLabel(filteredSymptom.factor, subcategory)}{" "}
                                                    <button
                                                        onClick={() => {
                                                            addPauseFactor(filteredSymptom.factor);
                                                            removeTracking(filteredSymptom.factor);
                                                        }}
                                                        style={{ background: '#da5033' }}
                                                    >
                                                        {t("dashboard.pause")}
                                                    </button>
                                                </li>
                                            ))}
                                    </ul>
                                </Tab.Pane>
                            ))}
                        </Tab.Content>
                    </Col>
                </Row>
            </Tab.Container>
        </Container>
    )
}

export default UITrackingList